import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import NiceModal, { useModal } from "@ebay/nice-modal-react";
import { HStack, VStack } from "../../../../styled-system/jsx";
import { Modal } from "../../../components/core/basic/Modal";
import { Button } from "../../../components/core/basic/Button";
import { TagMultiSelect } from "./TagMultiSelect";
import type { TagEntityType } from "../../../zodios/apiDefinitions/api/tags";

interface EntityTagsModalProps {
  entityType: TagEntityType;
  title: string;
  initialTagIds?: number[];
  onSubmit: (tagIds: number[]) => Promise<void> | void;
}

export const EntityTagsFormModal = NiceModal.create(
  ({ entityType, title, initialTagIds, onSubmit }: EntityTagsModalProps) => {
    const { t } = useTranslation(["tags", "common"]);
    const modal = useModal();
    const initial = useMemo(() => initialTagIds ?? [], [initialTagIds]);
    const [selectedIds, setSelectedIds] = useState(initial);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleConfirm = async () => {
      setIsSubmitting(true);
      try {
        await onSubmit(selectedIds);
        modal.remove();
      } finally {
        setIsSubmitting(false);
      }
    };

    const initialSet = useMemo(() => new Set(initial), [initial]);
    const isUnchanged = selectedIds.length === initial.length && selectedIds.every((id) => initialSet.has(id));

    return (
      <Modal
        isOpen={modal.visible}
        isDismissable
        onOpenChange={(isOpen) => !isOpen && modal.remove()}
        title={title}
        css={{ minWidth: "40vw" }}
      >
        <VStack gap="large" alignItems="stretch" paddingY="medium" paddingX="xlarge">
          <TagMultiSelect entityType={entityType} value={selectedIds} onChange={setSelectedIds} />
          <HStack justifyContent="space-around">
            <Button variant="secondary" onPress={() => modal.remove()} isDisabled={isSubmitting}>
              {t(($) => $.common.actions.Cancel)}
            </Button>
            <Button onPress={handleConfirm} isDisabled={isUnchanged} isPending={isSubmitting}>
              {t(($) => $.common.actions.Submit)}
            </Button>
          </HStack>
        </VStack>
      </Modal>
    );
  },
);
